import { useEffect } from 'react';
import { CaretLeft, CaretRight, Check } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '~/components/ui/dialog';
import { pickGateState } from './design-direction-gate';
import { DesignSuggestionImage } from './design-suggestion-image';
import { stepViewerIndex, viewerPickButtonLabel } from './design-direction-viewer';
// (pure logic lives in the sibling `design-direction-viewer.ts` — this file
// is `.tsx` and named `design-suggestion-viewer` on purpose, so an import of
// './design-direction-viewer' can never ambiguously resolve to itself.)

/**
 * FRONT-2382 (feedback round 2) — the "zoomed" viewer for a design
 * suggestion card or a staged reference thumbnail: the image as large as
 * the screen reasonably allows, its title/description underneath, and (for
 * suggestions only) a primary pick button reusing the SAME `pickGateState`
 * the small per-card control uses — a viewer that could pick past the cap
 * the grid enforces would be its own bug, not a feature.
 *
 * `pick` is omitted entirely for the Project-context dialog's single-
 * reference viewer, which also never has more than one item, so the arrows
 * never render there either — one component renders both surfaces so they
 * cannot drift apart.
 *
 * Built on the shared `Dialog` primitive (Radix) so it stacks correctly
 * above the coding build window and traps focus; Escape closes it via the
 * primitive's own `onOpenChange`, with no extra handling needed here.
 */

export interface DesignSuggestionViewerItem {
  id: string;
  title: string;
  description?: string;
  imageUrl: string;
}

export interface DesignSuggestionViewerPick {
  isPicked: (id: string) => boolean;
  pickedCount: number;
  onTogglePick: (item: DesignSuggestionViewerItem) => void;
}

export function DesignSuggestionViewer({
  items,
  index,
  onIndexChange,
  onClose,
  pick,
}: {
  items: DesignSuggestionViewerItem[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  pick?: DesignSuggestionViewerPick;
}) {
  const showArrows = items.length > 1;

  // Keyboard arrows move through every shown item, in the same on-screen
  // order the grid renders them in (`items` is that same array). Escape is
  // handled by the Dialog primitive itself via `onOpenChange`, not here.
  useEffect(() => {
    if (!showArrows) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') onIndexChange(stepViewerIndex(index, items.length, -1));
      else if (e.key === 'ArrowRight') onIndexChange(stepViewerIndex(index, items.length, 1));
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [index, items.length, showArrows, onIndexChange]);

  const item = items[index];
  if (!item) return null;

  const picked = pick?.isPicked(item.id) ?? false;
  const gate = pick ? pickGateState({ picked, pickedCount: pick.pickedCount }) : undefined;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="design-suggestion-viewer"
        className="flex h-[90vh] w-[95vw] max-w-[1400px] flex-col items-center gap-3 sm:max-w-[1400px]"
      >
        {/* A dialog needs an accessible title; the visible title/description
            pair below duplicates this for sighted users, so this copy is
            screen-reader only. */}
        <DialogTitle className="sr-only">{item.title}</DialogTitle>
        <DialogDescription className="sr-only">{item.description ?? item.title}</DialogDescription>

        <div className="relative flex min-h-0 w-full flex-1 items-center justify-center">
          {showArrows && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Previous direction"
              className="absolute left-2 top-1/2 -translate-y-1/2"
              onClick={() => onIndexChange(stepViewerIndex(index, items.length, -1))}
            >
              <CaretLeft />
            </Button>
          )}
          {/* Sized against the flex box that's actually left over inside
              this dialog (`flex-1 min-h-0` above), never against the raw
              viewport — a `vh`-based cap ignores how much the title,
              description and pick button beneath it already consume, so a
              portrait image could still grow past the dialog's own edge and
              paint over them (live finding, `diag2-blocked.png`). `h-full
              w-full` gives the image a definite box to fit inside;
              `max-h-full max-w-full object-contain` is what stops it from
              ever exceeding that box, in either dimension, without cropping
              or upscaling past its source. */}
          <DesignSuggestionImage
            src={item.imageUrl}
            alt={item.title}
            className="h-full w-full max-h-full max-w-full object-contain"
            testId="design-suggestion-viewer-image"
          />
          {showArrows && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Next direction"
              className="absolute right-2 top-1/2 -translate-y-1/2"
              onClick={() => onIndexChange(stepViewerIndex(index, items.length, 1))}
            >
              <CaretRight />
            </Button>
          )}
        </div>

        <div className="w-full shrink-0 text-center">
          <p
            data-testid="design-suggestion-viewer-title"
            className="text-[length:var(--text-sm)] font-medium text-foreground"
          >
            {item.title}
          </p>
          {item.description && (
            <p className="text-[length:var(--text-xs)] text-muted-foreground [text-wrap:pretty]">
              {item.description}
            </p>
          )}
        </div>

        {pick && (
          <div className="flex shrink-0 flex-col items-center gap-1">
            <Button
              type="button"
              variant={picked ? 'outline' : 'default'}
              disabled={gate?.disabled}
              onClick={() => pick.onTogglePick(item)}
              className="gap-1.5"
            >
              {picked && <Check size={14} weight="bold" />}
              {viewerPickButtonLabel(picked)}
            </Button>
            {/* Visible text, not just a hover hint — same disabled-control
                rule as the cap reason under the grid header. */}
            {gate?.disabled && <p className="text-[length:var(--text-xs)] text-muted-foreground">{gate.reason}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
